import React, { useRef, useState, useCallback } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { ErrorList, TableStyle, TableTitle, Title } from '../../../style';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import { tableSections as defaultTableSections } from '../../../consts/tableSections';
import { Errors } from '../../ReportTables/style';
import { FundErrorsDisplay } from '../../../../components/FundErrorsDisplay';
import { idCreator } from '../../../../../Project/utils/idCreator';

export const AIF002Reports = ({
  tableSections = defaultTableSections,
  isStaticTemplate,
  targetRef,
  funds,
  currentDataKey,
  onChangeFund,
  schema,
  addRow,
  onDeleteRow,
  isMultiFunds,
  sectionRefs,
  virtuosoRef,
  onChangeActiveSection,
  onScrollingChange,
  isViewer
}) => {
  const [expandIds, setExpandIds] = useState([]);

  // Horizontal scroll sync across section tables.
  // `containerRef` scopes the lookup to this report; `scrollLeftRef` holds the
  // shared position. We sync via a single rAF-throttled handler over the few
  // tables Virtuoso keeps mounted — no per-element listeners, observers, or timers.
  const containerRef = useRef(null);
  const scrollLeftRef = useRef(0);
  const isSyncingRef = useRef(false);

  const handleTableScroll = useCallback(e => {
    const { scrollLeft } = e.currentTarget;
    // Ignore the echo from programmatically syncing the other tables.
    if (scrollLeft === scrollLeftRef.current) return;
    scrollLeftRef.current = scrollLeft;
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    requestAnimationFrame(() => {
      isSyncingRef.current = false;
      const tables = containerRef.current?.querySelectorAll('.scrolling-table');
      tables?.forEach(table => {
        if (table.scrollLeft !== scrollLeftRef.current) {
          table.scrollLeft = scrollLeftRef.current;
        }
      });
    });
  }, []);

  const renderSection = useCallback(
    (index, onOpen, ids) => {
      const { key, title, Component, hasAddDelete } = tableSections[index];
      const isRequiredTable =
        funds[currentDataKey]?.required_tables?.[key]?.field_names?.length === 0;

      const errors = funds[currentDataKey]?.fund_data?.[key]?.find(item => item?.errors?.general)
        ?.errors?.general;

      const errorsWithFunds = isMultiFunds
        ? Object.values(funds)
            .map(item => ({
              name: item.fund_name,
              errors: item?.fund_data?.[key]?.find(f => f?.errors?.general)?.errors?.general,
              isRequiredTable: item?.required_tables?.[key]?.field_names?.length === 0,
              fund_id: item.fund_id
            }))
            .filter(value => !!value.errors || !!value.isRequiredTable)
        : [];

      return (
        <div
          key={key}
          id={`fca-aif002-section-${key}`}
          data-section-key={key}
          ref={el => (sectionRefs.current[key] = el)}
        >
          {title && (
            <TableTitle onClick={() => onOpen(key)} id={key}>
              {!ids.includes(key?.replace(/\d+/g, '')) ? (
                <IoIosArrowDown fontSize="16px" />
              ) : (
                <IoIosArrowUp fontSize="16px" />
              )}
              {title}
            </TableTitle>
          )}

          {!ids.includes(key) && !isViewer && !isMultiFunds && (
            <Errors id={`fca-aif002-errors-${key}`}>
              {isRequiredTable && (
                <li id={`fca-aif002-required-error-${key}`}>This section must be completed</li>
              )}
              {errors?.map(item => (
                <li key={item.msg} id={`fca-aif002-error-${key}-${idCreator(item.msg)}`}>
                  {item.msg}
                </li>
              ))}
            </Errors>
          )}

          {!ids.includes(key?.replace(/\d+/g, '')) && (
            <TableStyle
              id={`fca-aif002-table-section-${key}`}
              style={{ position: 'relative' }}
              className="prevent-split scrolling-table"
              onScroll={handleTableScroll}
              ref={el => {
                // Align a freshly-mounted table with the shared scroll position.
                if (el) el.scrollLeft = scrollLeftRef.current;
              }}
            >
              {!ids.includes(key) && !isViewer && isMultiFunds && errorsWithFunds?.length > 0 && (
                <FundErrorsDisplay
                  funds={funds}
                  errorsWithFunds={errorsWithFunds}
                  columnMinWidthOffset={80}
                  columnWidth={280}
                />
              )}
              <Component
                key={`${key}-${currentDataKey}`}
                currentDataKey={currentDataKey}
                funds={funds}
                onChangeFund={onChangeFund}
                schema={schema}
                isStaticTemplate={isStaticTemplate}
                isMultiFunds={isMultiFunds}
                onAddColumn={hasAddDelete ? addRow : undefined}
                onDeleteColumn={hasAddDelete ? onDeleteRow : undefined}
              />
            </TableStyle>
          )}
        </div>
      );
    },
    [
      funds,
      currentDataKey,
      isViewer,
      isMultiFunds,
      schema,
      onChangeFund,
      isStaticTemplate,
      addRow,
      onDeleteRow,
      sectionRefs,
      handleTableScroll
    ]
  );

  const onChangeIndex = useCallback(
    value => {
      const currentSection = tableSections[value.startIndex];
      if (currentSection) {
        onChangeActiveSection(currentSection.key);
      }
    },
    [onChangeActiveSection, tableSections]
  );

  const onOpenTable = useCallback(id => {
    setExpandIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(val => val !== id);
      } else {
        return [...prev, id];
      }
    });
  }, []);

  const itemContent = useCallback(
    index => renderSection(index, onOpenTable, expandIds),
    [renderSection, onOpenTable, expandIds]
  );

  return (
    <div
      className="tables k-grid"
      id={isStaticTemplate ? 'static-table' : 'pdfContainer'}
      ref={el => {
        containerRef.current = el;
        if (targetRef) targetRef.current = el;
      }}
      style={{
        height: isStaticTemplate
          ? isMultiFunds
            ? 'calc(100% - 60px)'
            : 'calc(100% - 114px)'
          : isMultiFunds
            ? 'calc(100% - 24px)'
            : 'calc(100% - 84px)'
      }}
    >
      {funds[currentDataKey]?.report_status?.errors?.length > 0 && (
        <ErrorList id="fca-aif002-report-status-errors">
          <IoIosArrowUp id="fca-aif002-report-status-toggle" />
          <div className="body" id="fca-aif002-report-status-body">
            <div className="header" id="fca-aif002-report-status-header">
              <p className="title" id="fca-aif002-report-status-title">
                Submission status:
              </p>
              <p
                className="status"
                id="fca-aif002-report-status-badge"
                style={{ background: '#DF4141' }}
              >
                Not submitted
              </p>
            </div>
            <ul id="fca-aif002-report-status-list">
              {funds[currentDataKey].report_status.errors.map(item => (
                <li
                  key={item.reference_key}
                  id={`fca-aif002-report-status-error-${idCreator(item.reference_key)}`}
                >
                  <span
                    className="underline"
                    id={`fca-aif002-report-status-reference-${idCreator(item.reference_key)}`}
                  >
                    Reference: {item.reference_key}
                  </span>
                  , Error message:{' '}
                  <i id={`fca-aif002-report-status-message-${idCreator(item.reference_key)}`}>
                    {item.error_message}
                  </i>
                </li>
              ))}
            </ul>
          </div>
        </ErrorList>
      )}
      <Title id="report_title" style={{ display: 'none' }}>
        AIF002 - Report on an AIF
      </Title>
      <Virtuoso
        style={{
          height: '100%',
          width: '100%'
        }}
        totalCount={tableSections.length}
        rangeChanged={onChangeIndex}
        isScrolling={onScrollingChange}
        itemContent={itemContent}
        overscan={0}
        ref={virtuosoRef}
      />
    </div>
  );
};
